import { useMemo, useState } from 'react';
import axios from 'axios';
import { Sparkles, Download, RotateCcw, Folder, FileText } from 'lucide-react';
import { generateScaffoldApi, downloadScaffoldApi } from '@/features/projects/api';
import type { ProjectScaffold } from '@/features/projects/types';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { getApiErrorMessage } from '@/lib/api-client';

export default function ProjectBuilderPage() {
  const toast = useToast();

  const [description, setDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [scaffold, setScaffold] = useState<ProjectScaffold | null>(null);
  const [projectName, setProjectName] = useState('');
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());

  const handleGenerate = async () => {
    const desc = description.trim();
    if (!desc || generating) return;
    setGenerating(true);
    try {
      const result = await generateScaffoldApi(desc);
      setScaffold(result);
      setProjectName(result.projectName);
      setSelectedAgents(new Set(result.agents.map((a) => a.slug)));
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 503) {
        toast.error('Geração indisponível: configure a Claude API.');
      } else {
        toast.error(getApiErrorMessage(err, 'Falha ao gerar a arquitetura.'));
      }
    } finally {
      setGenerating(false);
    }
  };

  const toggleAgent = (slug: string) => {
    setSelectedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const handleDownload = async () => {
    if (!scaffold || downloading) return;
    setDownloading(true);
    try {
      const finalScaffold: ProjectScaffold = {
        ...scaffold,
        projectName: projectName.trim() || scaffold.projectName,
        agents: scaffold.agents.filter((a) => selectedAgents.has(a.slug)),
      };
      const { blob, filename } = await downloadScaffoldApi(finalScaffold);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Falha ao baixar o projeto.'));
    } finally {
      setDownloading(false);
    }
  };

  const handleReset = () => {
    setScaffold(null);
    setProjectName('');
    setSelectedAgents(new Set());
  };

  return (
    <div className="max-w-4xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Gerador de projeto</h1>
        <p className="text-text-muted text-sm">
          Descreva o que você quer construir e gere uma arquitetura inicial com subagentes sugeridos.
        </p>
      </header>

      {!scaffold ? (
        <Card>
          <CardBody className="space-y-5">
            <Textarea
              label="O que você vai construir?"
              placeholder="Descreva o projeto que você vai construir..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[200px] font-sans"
            />
            <div className="flex justify-end">
              <Button onClick={handleGenerate} loading={generating} disabled={!description.trim()}>
                <Sparkles size={16} /> Gerar arquitetura
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Projeto */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">Projeto</h2>
            </CardHeader>
            <CardBody className="space-y-4">
              <Input
                label="Nome do projeto (pasta raiz)"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                hint="Você pode renomear a pasta antes de baixar."
              />
              <p className="text-sm text-text-secondary">{scaffold.summary}</p>
            </CardBody>
          </Card>

          {/* Subagentes sugeridos */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">Subagentes sugeridos</h2>
              <p className="text-sm text-text-muted">
                Marque os que devem ser incluídos no download.
              </p>
            </CardHeader>
            <CardBody>
              {scaffold.agents.length === 0 ? (
                <p className="text-sm text-text-muted">Nenhum subagente sugerido.</p>
              ) : (
                <ul className="space-y-2">
                  {scaffold.agents.map((a) => (
                    <li key={a.slug}>
                      <label className="flex items-start gap-3 rounded-lg border border-border bg-surface-base p-3 cursor-pointer hover:border-brand-400 transition">
                        <input
                          type="checkbox"
                          checked={selectedAgents.has(a.slug)}
                          onChange={() => toggleAgent(a.slug)}
                          className="mt-1 h-4 w-4 accent-brand-500"
                        />
                        <div className="flex-1">
                          <Badge tone="brand">{a.slug}</Badge>
                          <p className="mt-1 text-sm text-text-secondary">{a.reason}</p>
                        </div>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          {/* Estrutura de arquivos */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">Estrutura de arquivos</h2>
            </CardHeader>
            <CardBody>
              <ul className="font-mono text-sm">
                {scaffold.files.map((f) => {
                  const depth = (f.path.match(/\//g) ?? []).length;
                  const name = f.path.split('/').filter(Boolean).pop() ?? f.path;
                  return (
                    <li
                      key={f.path}
                      className="flex items-center gap-2 py-0.5 text-text-secondary"
                      style={{ paddingLeft: `${depth * 1.25}rem` }}
                    >
                      {f.directory ? (
                        <Folder size={14} className="text-brand-400 shrink-0" />
                      ) : (
                        <FileText size={14} className="text-text-muted shrink-0" />
                      )}
                      <span className={f.directory ? 'text-text-primary' : ''}>
                        {name}
                        {f.directory ? '/' : ''}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </CardBody>
          </Card>

          {/* README.md */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">README.md</h2>
            </CardHeader>
            <CardBody>
              <pre className="max-h-96 overflow-auto rounded-lg border border-border bg-surface-base p-4 text-xs text-text-secondary whitespace-pre-wrap">
                {scaffold.readme}
              </pre>
            </CardBody>
          </Card>

          <div className="flex flex-wrap gap-3 justify-end">
            <Button variant="ghost" onClick={handleReset}>
              <RotateCcw size={16} /> Recomeçar
            </Button>
            <Button onClick={handleDownload} loading={downloading}>
              <Download size={16} /> Baixar projeto (.zip)
            </Button>
          </div>
        </div>
      )}

      {generating && (
        <div className="flex items-center justify-center gap-2 py-4 text-text-muted">
          <Spinner size={18} /> Gerando arquitetura...
        </div>
      )}
    </div>
  );
}
